import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { GlobalHeader } from "@/components/GlobalHeader";
import { AdminRoute } from "@/components/admin/AdminRoute";
import { AdminLayout } from "@/components/admin/AdminLayout";
import ErrorBoundary from "@/components/ErrorBoundary";
import { CookieConsent } from "@/components/CookieConsent";
import GoogleAnalytics from "@/components/GoogleAnalytics";
import { Suspense } from "react";
import { lazyRetry } from "@/lib/lazyRetry";
import Index from "./pages/Index";
import PaywallGate from "@/components/paywall/PaywallGate";
import { useVersionCheck } from "@/hooks/useVersionCheck";

// Lazy-loaded routes
const Look = lazyRetry(() => import("./pages/Look"), "Look");
const Test = lazyRetry(() => import("./pages/Test"), "Test");
const Resultado = lazyRetry(() => import("./pages/Resultado"), "Resultado");
const Curso = lazyRetry(() => import("./pages/Curso"), "Curso");
const PerfilVisual = lazyRetry(() => import("./pages/PerfilVisual"), "PerfilVisual");
const Consultores = lazyRetry(() => import("./pages/Consultores"), "Consultores");
const ConsultorDashboard = lazyRetry(() => import("./pages/ConsultorDashboard"), "ConsultorDashboard");
const GuiasEbooks = lazyRetry(() => import("./pages/GuiasEbooks"), "GuiasEbooks");
const Privacidade = lazyRetry(() => import("./pages/Privacidade"), "Privacidade");
const TermosDeUso = lazyRetry(() => import("./pages/TermosDeUso"), "TermosDeUso");
const AdminDashboard = lazyRetry(() => import("./pages/AdminDashboard"), "AdminDashboard");
const AdminUsers = lazyRetry(() => import("./pages/AdminUsers"), "AdminUsers");
const AdminUserDetail = lazyRetry(() => import("./pages/AdminUserDetail"), "AdminUserDetail");
const AdminLogs = lazyRetry(() => import("./pages/AdminLogs"), "AdminLogs");
const AdminGrandeTeste = lazyRetry(() => import("./pages/AdminGrandeTeste"), "AdminGrandeTeste");
const AdminGrandeTesteGrupo = lazyRetry(() => import("./pages/AdminGrandeTesteGrupo"), "AdminGrandeTesteGrupo");
const AdminTesteExpress = lazyRetry(() => import("./pages/AdminTesteExpress"), "AdminTesteExpress");
const AdminAIModels = lazyRetry(() => import("./pages/AdminAIModels"), "AdminAIModels");
const ResetPassword = lazyRetry(() => import("./pages/ResetPassword"), "ResetPassword");
const NotFound = lazyRetry(() => import("./pages/NotFound"), "NotFound");
const GrandeTeste = lazyRetry(() => import("./pages/GrandeTeste"), "GrandeTeste");
const MapaDeEstilo = lazyRetry(() => import("./pages/MapaDeEstilo"), "MapaDeEstilo");
const MeusDados = lazyRetry(() => import("./pages/MeusDados"), "MeusDados");
const IncidentesLGPD = lazyRetry(() => import("./pages/IncidentesLGPD"), "IncidentesLGPD");
const Vimeo = lazyRetry(() => import("./pages/Vimeo"), "Vimeo");
const GrandeTesteVenda = lazyRetry(() => import("./pages/GrandeTesteVenda"), "GrandeTesteVenda");
const Youtube = lazyRetry(() => import("./pages/Youtube"), "Youtube");
const Precos = lazyRetry(() => import("./pages/Precos"), "Precos");
const MinhaAssinatura = lazyRetry(() => import("./pages/MinhaAssinatura"), "MinhaAssinatura");
const ExemploResultado1 = lazyRetry(() => import("./pages/ExemploResultado1"), "ExemploResultado1");
const ExemploResultado2 = lazyRetry(() => import("./pages/ExemploResultado2"), "ExemploResultado2");

const AdminProdutos = lazyRetry(() => import("./pages/AdminProdutos"), "AdminProdutos");
const AdminCompras = lazyRetry(() => import("./pages/AdminCompras"), "AdminCompras");

const queryClient = new QueryClient();

function AppContent() {
  const location = useLocation();
  useVersionCheck();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const hideHeader = isAdminRoute || location.pathname === '/app';

  return (
    <ErrorBoundary>
      <GoogleAnalytics />
      {!hideHeader && <GlobalHeader />}
      <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>}>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/app" element={<Look />} />
          <Route path="/teste" element={<Test />} />
          <Route path="/grande-teste" element={<PaywallGate productKey="grande_teste"><GrandeTeste /></PaywallGate>} />
          <Route path="/grande-teste-mapa-de-estilo" element={<GrandeTesteVenda />} />
          <Route path="/mapa-de-estilo" element={<PaywallGate productKey="grande_teste"><MapaDeEstilo /></PaywallGate>} />
          <Route path="/resultado" element={<Resultado />} />
          <Route path="/curso" element={<Curso />} />
          <Route path="/perfil-visual" element={<PerfilVisual />} />
          <Route path="/consultores" element={<Consultores />} />
          <Route path="/consultor" element={<ConsultorDashboard />} />
          <Route path="/guias-ebooks" element={<GuiasEbooks />} />
          <Route path="/privacidade" element={<Privacidade />} />
          <Route path="/termos-de-uso" element={<TermosDeUso />} />
          <Route path="/meus-dados" element={<MeusDados />} />
          <Route path="/incidentes-lgpd" element={<IncidentesLGPD />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/vimeo" element={<Vimeo />} />
          <Route path="/youtube" element={<Youtube />} />
          <Route path="/precos" element={<Precos />} />
          <Route path="/minha-assinatura" element={<MinhaAssinatura />} />
          <Route path="/exemplo-resultado-1" element={<ExemploResultado1 />} />
          <Route path="/exemplo-resultado-2" element={<ExemploResultado2 />} />


          {/* Admin routes */}
          <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
            <Route index element={<AdminDashboard />} />
            <Route path="usuarios" element={<AdminUsers />} />
            <Route path="usuarios/:id" element={<AdminUserDetail />} />
            <Route path="logs" element={<AdminLogs />} />
            <Route path="grande-teste" element={<AdminGrandeTeste />} />
            <Route path="grande-teste/:grupoId" element={<AdminGrandeTesteGrupo />} />
            <Route path="teste-express" element={<AdminTesteExpress />} />
            <Route path="modelos-ia" element={<AdminAIModels />} />
            <Route path="produtos" element={<AdminProdutos />} />
            <Route path="compras" element={<AdminCompras />} />
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      <CookieConsent />
    </ErrorBoundary>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AuthProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
